import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { BadgeCheck, Phone, ArrowUpRight, ExternalLink } from "lucide-react";
import { Reveal, Btn, Overline, MaskedLine } from "@/components/motion-bits";
import { Label, Input, Select, TextArea } from "@/components/Field";
import driverImg from "@/assets/careers_driver.jpg";
import driverTwoImg from "@/assets/careers_driver_two.jpg";
import driverThreeImg from "@/assets/careers_driver_three.jpg";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTELLIAPP_URL = process.env.REACT_APP_INTELLIAPP_URL || "";
const INTELLIAPP_CONFIGURED =
  Boolean(INTELLIAPP_URL) && !INTELLIAPP_URL.includes("REPLACE-WITH");

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  division: "regional",
  experience: "",
  cdl_class: "CDL-A",
  message: "",
};

const REQUIREMENTS = [
  { title: "Valid CDL-A", copy: "A clean commercial license you're proud of." },
  { title: "Safety-first record", copy: "Reliance starts with a record that shows it." },
  { title: "Ready to run", copy: "Pick your lane — regional weekends or OTR miles." },
];

const Careers = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/applications`, form);
      setSent(true);
      toast.success("Application received — dispatch will call you.", {
        duration: 5000,
      });
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong. Try again or call 833-V1aduct (833-812-3828).");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="careers-page">
      {/* Hero */}
      <section className="px-6 lg:px-10 py-16 md:py-24">
        <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <Overline className="text-brand-red">
              <MaskedLine delay={0.1}>Careers</MaskedLine>
            </Overline>
            <h1 className="mt-4 font-display uppercase tracking-tight leading-[0.9] text-[clamp(3rem,8vw,7rem)]">
              <MaskedLine delay={0.24}>Drive for</MaskedLine>
              <MaskedLine delay={0.38}>
                <span className="text-brand-gold">the viaduct.</span>
              </MaskedLine>
            </h1>
            <Reveal delay={0.5}>
              <p className="mt-8 max-w-xl text-lg leading-relaxed text-ink/75">
                Whether you want your weekends or the long miles, there's a seat at Memphis
                Viaduct Inc with home time you can actually plan around — regional runs home
                every weekend, OTR comes home three days after two weeks out.
              </p>
            </Reveal>
            <Reveal delay={0.6}>
              <a
                href="tel:+18338123828"
                data-testid="careers-phone-chip"
                className="mt-8 inline-flex items-center gap-3 border-[3px] border-ink bg-ink text-cream px-5 py-3 font-display uppercase tracking-wide text-xl shadow-block transition-[transform,box-shadow] duration-200 hover:translate-x-[3px] hover:translate-y-[3px] hover:shadow-[2px_2px_0_#111]"
              >
                <Phone size={20} aria-hidden="true" /> 833-V1aduct · 833-812-3828
              </a>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={0.2}>
              <div className="-rotate-1 border-[3px] border-ink bg-cream p-2 shadow-blocklg">
                <img
                  src={driverImg}
                  alt="Illustration of a truck driver standing in front of a semi truck with the pyramid and bridge behind"
                  className="w-full aspect-square object-cover"
                  data-testid="careers-hero-illustration"
                />
                <p className="px-2 py-3 text-center font-display uppercase tracking-wide text-lg text-brand-red">
                  Seat's open · Pick your lane
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Lanes + requirements */}
      <section className="bg-paper border-y-[3px] border-ink px-6 lg:px-10 py-20 md:py-24">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-3 gap-8">
          <Reveal>
            <Link
              to="/services"
              data-testid="careers-regional-card"
              className="group block h-full border-[3px] border-ink bg-cream p-8 shadow-block transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-[10px_10px_0_#111]"
            >
              <Overline className="text-brand-red">Regional</Overline>
              <p className="mt-3 font-display uppercase tracking-tight text-3xl">Home every weekend.</p>
              <p className="mt-4 text-ink/70 leading-relaxed">
                Consistent lanes out of Memphis, in your own bed when the weekend hits.
              </p>
              <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-brand-red">
                The lane <ArrowUpRight size={18} aria-hidden="true" />
              </span>
            </Link>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/services"
              data-testid="careers-otr-card"
              className="group block h-full border-[3px] border-ink bg-cream p-8 shadow-block transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-[10px_10px_0_#111]"
            >
              <Overline className="text-brand-red">Over the Road</Overline>
              <p className="mt-3 font-display uppercase tracking-tight text-3xl">2 weeks out. 3 days home.</p>
              <p className="mt-4 text-ink/70 leading-relaxed">
                Country miles with a rhythm you can plan a life around.
              </p>
              <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-brand-red">
                The lane <ArrowUpRight size={18} aria-hidden="true" />
              </span>
            </Link>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="h-full border-[3px] border-ink bg-brand-tealdeep text-cream p-8 shadow-block" data-testid="careers-requirements-card">
              <Overline className="text-brand-gold">Bring</Overline>
              <ul className="mt-4 space-y-3">
                {REQUIREMENTS.map((r) => (
                  <li key={r.title} className="flex items-start gap-3">
                    <BadgeCheck size={22} className="mt-0.5 shrink-0 text-brand-gold" aria-hidden="true" />
                    <span>
                      <span className="font-display uppercase tracking-wide text-lg">{r.title}</span>
                      <span className="block text-sm text-cream/70">{r.copy}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Drivers gallery */}
      <section className="px-6 lg:px-10 py-20 md:py-24" data-testid="careers-drivers-section">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <Overline className="text-brand-red">The Drivers</Overline>
            <h2 className="mt-4 font-display uppercase tracking-tight text-4xl md:text-6xl">
              Every seat, the same promise.
            </h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12">
            <Reveal>
              <div className="-rotate-1 border-[3px] border-ink bg-cream p-2 shadow-blocklg transition-transform duration-300 hover:rotate-0">
                <img
                  src={driverImg}
                  alt="Illustration of a truck driver standing in front of a classic semi truck, the Memphis pyramid and bridge behind him"
                  className="w-full aspect-square object-cover"
                  data-testid="careers-driver-one-illustration"
                />
                <p className="px-2 py-3 text-center font-display uppercase tracking-wide text-lg text-brand-red">
                  Regional or OTR — your call
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="rotate-1 border-[3px] border-ink bg-cream p-2 shadow-blocklg transition-transform duration-300 hover:rotate-0">
                <img
                  src={driverTwoImg}
                  alt="Illustration of a Latina truck driver beside her sleeper-cab semi truck, the Memphis pyramid and bridge behind her"
                  className="w-full aspect-square object-cover"
                  data-testid="careers-driver-two-illustration"
                />
                <p className="px-2 py-3 text-center font-display uppercase tracking-wide text-lg text-brand-teal">
                  Different roads, same reliance
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="-rotate-1 border-[3px] border-ink bg-cream p-2 shadow-blocklg transition-transform duration-300 hover:rotate-0">
                <img
                  src={driverThreeImg}
                  alt="Illustration of a white truck driver with a beard opening the cab door of his semi truck, the Memphis pyramid and bridge behind him"
                  className="w-full aspect-square object-cover"
                  data-testid="careers-driver-three-illustration"
                />
                <p className="px-2 py-3 text-center font-display uppercase tracking-wide text-lg text-brand-red">
                  One badge, every road
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Application form */}
      <section className="px-6 lg:px-10 py-20 md:py-28">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <Overline className="text-brand-red">Driver Application</Overline>
            <h2 className="mt-4 font-display uppercase tracking-tight text-4xl md:text-6xl">
              Apply to the viaduct.
            </h2>
            <p className="mt-4 max-w-2xl text-lg text-ink/70">
              Two ways in: the full IntelliApp — the industry-standard application powered by
              Tenstreet — or our quick application below. For more info call 833-V1aduct
              (833-812-3828).
            </p>
          </Reveal>

          <div className="mt-10 grid lg:grid-cols-12 gap-10">
            <Reveal delay={0.1} className="lg:col-span-5">
              <div className="h-full border-[3px] border-ink bg-brand-tealdeep text-cream p-6 md:p-8 shadow-blocklg" data-testid="intelliapp-card">
                <Overline className="text-brand-gold">Fast Track</Overline>
                <h3 className="mt-3 font-display uppercase tracking-tight text-3xl">
                  Apply with IntelliApp
                </h3>
                <p className="mt-4 leading-relaxed text-cream/75">
                  The trucking industry's standard application, powered by Tenstreet. Fill in
                  your full work history once — it saves your progress and works on any device.
                </p>
                <ul className="mt-5 space-y-2 text-sm text-cream/70">
                  <li>· Full application, mobile-friendly</li>
                  <li>· Save your progress &amp; digital signatures</li>
                  <li>· Goes straight to our recruiting desk</li>
                </ul>
                <div className="mt-8">
                  {INTELLIAPP_CONFIGURED ? (
                    <Btn href={INTELLIAPP_URL} external variant="gold" testid="intelliapp-start-button">
                      Start IntelliApp <ExternalLink size={20} aria-hidden="true" />
                    </Btn>
                  ) : (
                    <Btn
                      variant="gold"
                      testid="intelliapp-start-button"
                      onClick={() =>
                        toast.info(
                          "Our IntelliApp link is being set up — call 833-V1aduct (833-812-3828) or use the quick application below.",
                          { duration: 6000 },
                        )
                      }
                    >
                      Start IntelliApp <ExternalLink size={20} aria-hidden="true" />
                    </Btn>
                  )}
                </div>
                <p className="mt-5 text-xs font-bold uppercase tracking-[0.2em] text-cream/50">
                  Prefer to start here? Use the quick application →
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.15} className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="careers-application-form"
              className="mt-10 border-[3px] border-ink bg-cream p-6 md:p-10 shadow-blocklg"
            >
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="apply-name">Full name</Label>
                  <Input id="apply-name" name="name" required value={form.name} onChange={set("name")} placeholder="Dale Rivers" data-testid="apply-name-input" />
                </div>
                <div>
                  <Label htmlFor="apply-email">Email</Label>
                  <Input id="apply-email" name="email" type="email" required value={form.email} onChange={set("email")} placeholder="you@example.com" data-testid="apply-email-input" />
                </div>
                <div>
                  <Label htmlFor="apply-phone">Phone</Label>
                  <Input id="apply-phone" name="phone" type="tel" required value={form.phone} onChange={set("phone")} placeholder="(901) 555-0123" data-testid="apply-phone-input" />
                </div>
                <div>
                  <Label htmlFor="apply-division">Division</Label>
                  <Select id="apply-division" name="division" value={form.division} onChange={set("division")} data-testid="apply-division-select">
                    <option value="regional">Regional — home every weekend</option>
                    <option value="otr">OTR — 2 weeks out, 3 days home</option>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="apply-experience">Years of experience</Label>
                  <Input id="apply-experience" name="experience" type="number" min="0" max="60" value={form.experience} onChange={set("experience")} placeholder="5" data-testid="apply-experience-input" />
                </div>
                <div>
                  <Label htmlFor="apply-cdl">License held</Label>
                  <Select id="apply-cdl" name="cdl_class" value={form.cdl_class} onChange={set("cdl_class")} data-testid="apply-cdl-select">
                    <option value="CDL-A">CDL-A</option>
                    <option value="CDL-B">CDL-B</option>
                    <option value="Permit">Permit</option>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="apply-message">Anything else? (optional)</Label>
                  <TextArea id="apply-message" name="message" value={form.message} onChange={set("message")} placeholder="Routes you've run, equipment you know…" data-testid="apply-message-input" />
                </div>
              </div>

              {sent && (
                <div className="mt-6 border-[3px] border-ink bg-brand-gold/20 p-4 font-semibold" data-testid="apply-success-panel">
                  Application received — dispatch will call you. For anything urgent, call
                  833-V1aduct (833-812-3828).
                </div>
              )}

              <div className="mt-8">
                <Btn type="submit" variant="red" testid="apply-submit-button" disabled={sending}>
                  {sending ? "Sending…" : "Send Application"}
                </Btn>
              </div>
            </form>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Careers;
